import { useCallback, useState } from "react";
import { MAX_PROJECT_MOVE_ITEMS, type ProjectMoveResult } from "@/lib/api";
import { chunkIds, mergeMoveResults, moveSummary, undoGroups } from "@/lib/projectMove";

type Mover = (ids: string[], projectId: string | null) => Promise<ProjectMoveResult>;

export interface MoveRequest {
  ids: string[];
  /** Selected items the caller may not move (another member's); reported as skipped. */
  skipped: number;
  /** Where every requested item already is; undefined when they differ. */
  currentProjectId: string | null | undefined;
}

export interface MoveOutcome {
  result: ProjectMoveResult;
  message: string;
}

/**
 * Move-to-project flow shared by Gallery, Studio and Elements: the picker
 * request, a chunked move, the result message and a one-step Undo.
 */
export function useProjectMove({ move, onMoved }: { move: Mover; onMoved: () => unknown }) {
  const [request, setRequest] = useState<MoveRequest | null>(null);
  const [outcome, setOutcome] = useState<MoveOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((ids: string[], options: { skipped?: number; currentProjectId?: string | null } = {}) => {
    setError(null);
    setRequest({ ids, skipped: options.skipped ?? 0, currentProjectId: options.currentProjectId });
  }, []);

  const close = useCallback(() => {
    if (!busy) setRequest(null);
  }, [busy]);

  const confirm = useCallback(async (projectId: string | null, targetName: string) => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      const results: ProjectMoveResult[] = [];
      for (const ids of chunkIds(request.ids, MAX_PROJECT_MOVE_ITEMS)) results.push(await move(ids, projectId));
      const result = mergeMoveResults(results);
      setOutcome({ result, message: moveSummary(result, targetName, request.skipped) });
      setRequest(null);
      await onMoved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Items could not be moved. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [move, onMoved, request]);

  const undo = useCallback(async () => {
    if (!outcome) return;
    setBusy(true);
    try {
      for (const group of undoGroups(outcome.result)) {
        for (const ids of chunkIds(group.ids, MAX_PROJECT_MOVE_ITEMS)) await move(ids, group.projectId);
      }
      setOutcome(null);
      await onMoved();
    } catch (err) {
      setOutcome({ ...outcome, message: err instanceof Error ? `Undo failed: ${err.message}` : "Undo failed. Please try again." });
    } finally {
      setBusy(false);
    }
  }, [move, onMoved, outcome]);

  const dismiss = useCallback(() => setOutcome(null), []);

  return { request, outcome, busy, error, open, close, confirm, undo, dismiss };
}
